import { supabase } from './supabase';
import {
    getClaimedContactIdCore,
    listEventsForUserCore,
    listTodosByEventIdsCore,
    createTodoCore,
    assignUnassignedTodosToContactCore,
} from '@gv/core';

/**
 * Backfill to ensure calendar visibility:
 * - Assigns any of the user's unassigned todos to their "My Contact"
 * - Creates missing todos for contact_events owned by the user (linked by event_id)
 *
 * Safe to call repeatedly; de-duplicates based on existing event_id.
 */
export async function backfillTodosForUser(userId: string, forceRun: boolean = false): Promise<void> {
    // Run at most once per session for this user (unless forced)
    const key = `gv:backfill:todos:${userId}`;
    if (!forceRun && typeof window !== 'undefined' && sessionStorage.getItem(key) === 'done') {
        console.log('🔍 Backfill already ran this session for user:', userId);
        return;
    }

    console.log('🔍 Running backfill for user:', userId, forceRun ? '(forced)' : '');

    // 1) Resolve My Contact id via core
    const myContactId = await getClaimedContactIdCore(supabase as any, userId);

    // 2) Assign any of my unassigned todos to My Contact (if we have one)
    if (myContactId) {
        await assignUnassignedTodosToContactCore(supabase as any, userId, myContactId);
    }

    // 3) Create todos for ALL contact_events with a when_date that lack a linked todo
    const events = await listEventsForUserCore(supabase as any, userId, { withDateOnly: true });
    console.log(`🔍 Found ${events.length} contact events with dates for user`);
    const evIds = events.map((e) => e.id!).filter(Boolean) as string[];
    if (evIds.length === 0) {
        console.log('🔍 No contact events with dates found');
        if (typeof window !== 'undefined') sessionStorage.setItem(key, 'done');
        console.log('✅ Backfill completed for user:', userId);
        return;
    }

    const existing = await listTodosByEventIdsCore(supabase as any, evIds);
    const existingSet = new Set((existing || []).map((r: any) => r.event_id).filter(Boolean));
    console.log(`🔍 Found ${existingSet.size} existing todos for these events`);

    let created = 0;
    for (const e of events) {
        const eventId = e.id!;
        if (!eventId || existingSet.has(eventId)) continue;
        const title = (e.notes?.trim() || e.name || 'Follow-up').slice(0, 200);
        await createTodoCore(
            supabase as any,
            {
                title,
                description: `Auto-backfilled from event: ${e.name || ''}`.trim() || null,
                location: e.where || null,
                due_date: e.when ? new Date(e.when).toISOString() : null,
                done: false,
                contact_id: (e as any).whoContactId || null,
                event_id: eventId,
                assigned_contact_id: myContactId || null,
                group_id: null,
            } as any,
            userId
        );
        created++;
    }
    console.log(`✅ Backfill created ${created} new follow-up todos`);

    if (typeof window !== 'undefined') sessionStorage.setItem(key, 'done');
    console.log('✅ Backfill completed for user:', userId);
}
